import { CalendarDays, X } from 'lucide-react';
import { useState } from 'react';

import { usePreferences } from '@/stores/preferences-store';
import { cn } from '@/utils/cn';
import { formatDate } from '@/utils/format';

import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import type { PopoverPortalTarget } from './popover-portal';

/**
 * Date-only selection. Emits `YYYY-MM-DD` and never a timestamp.
 *
 * A contribution date, a birthday and a period end are facts about a calendar day,
 * not instants. Keeping them as plain date strings is what stops a contribution dated
 * the 1st from appearing in the previous month's report for anyone west of UTC.
 */

export interface DatePickerProps {
  value: string | null;
  onChange: (date: string | null) => void;
  placeholder?: string;
  min?: string;
  max?: string;
  disabledDates?: string[];
  markedDates?: string[];
  /** Quick choices above the grid — "Today", "Last Sunday", a period start. */
  presets?: { label: string; date: string }[];
  clearable?: boolean;
  clearLabel?: string;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  size?: 'sm' | 'md' | 'lg';
  popoverPortal?: PopoverPortalTarget;
  calendarLabels?: {
    month?: string;
    year?: string;
    previousMonth?: string;
    nextMonth?: string;
  };
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

const SIZES = {
  sm: 'h-8 px-2.5 text-body-sm',
  md: 'h-10 px-3 text-body',
  lg: 'h-12 px-4 text-card-title',
} as const;

export const DatePicker = ({
  value,
  onChange,
  placeholder,
  min,
  max,
  disabledDates,
  markedDates,
  presets,
  clearable = false,
  clearLabel,
  disabled = false,
  invalid = false,
  id,
  size = 'md',
  popoverPortal = 'body',
  calendarLabels,
  className,
  ...aria
}: DatePickerProps) => {
  const [open, setOpen] = useState(false);
  const locale = usePreferences((state) => state.locale);

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          className={cn(
            'flex w-full items-center justify-between gap-2 rounded-md border bg-surface text-left transition-colors',
            'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-fg-disabled',
            invalid ? 'border-danger' : 'border-border hover:border-border-strong',
            SIZES[size],
            className,
          )}
          {...aria}
        >
          <span className="flex min-w-0 items-center gap-2">
            <CalendarDays aria-hidden className="h-4 w-4 shrink-0 text-fg-tertiary" />
            <span className={cn('truncate', value ? 'text-fg' : 'text-fg-tertiary')}>
              {value ? formatDate(value, { locale }) : placeholder}
            </span>
          </span>

          {clearable && value && !disabled ? (
            <span
              role="button"
              tabIndex={-1}
              aria-label={clearLabel}
              onPointerDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onChange(null);
              }}
              className="rounded p-0.5 text-fg-tertiary hover:bg-surface-sunken hover:text-fg"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          ) : null}
        </button>
      </PopoverTrigger>

      <PopoverContent
        popoverPortal={popoverPortal}
        matchTriggerWidth={false}
        role="presentation"
        className="w-auto p-0"
      >
        {presets && presets.length > 0 ? (
          <div className="flex flex-wrap gap-1 border-b border-border p-2">
            {presets.map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => {
                  onChange(preset.date);
                  setOpen(false);
                }}
                className="rounded-md px-2 py-1 text-body-sm text-fg-secondary hover:bg-surface-sunken hover:text-fg"
              >
                {preset.label}
              </button>
            ))}
          </div>
        ) : null}

        <Calendar
          value={value}
          onChange={(date) => {
            onChange(date);
            setOpen(false);
          }}
          min={min}
          max={max}
          disabledDates={disabledDates}
          markedDates={markedDates}
          // Nested pickers portal into the same target as their parent, so a month
          // dropdown inside a calendar inside a dialog stays inside the dialog.
          popoverPortal={popoverPortal}
          monthLabel={calendarLabels?.month}
          yearLabel={calendarLabels?.year}
          previousMonthLabel={calendarLabels?.previousMonth}
          nextMonthLabel={calendarLabels?.nextMonth}
        />
      </PopoverContent>
    </Popover>
  );
};
